import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  bodyTargetUpstreamErrors,
  bodyUpstreamErrors,
  guardErrors,
  idParams,
  targetErrors,
} from '../common';
import { brainAnswerSchema, brainNoteSchema } from '../entities';
import { noteInput, questionInput } from '../inputs';

const c = initContract();

export const noteBody = z.object({ content: noteInput });
export type NoteBody = z.infer<typeof noteBody>;

export const askBody = z.object({ question: questionInput });
export type AskBody = z.infer<typeof askBody>;

/**
 * Two operations — remember a fact, ask about the facts — plus the means to see
 * and correct what was stored.
 *
 * `ask` is a POST because the question travels in a body and because answering
 * it costs money: nothing about it should look cacheable. Writing a note is
 * also a POST that can fail upstream, since a note is embedded before it is
 * stored and an unembedded note would be invisible to every future question.
 */
export const brainContract = c.router(
  {
    listNotes: {
      method: 'GET',
      path: '/notes',
      responses: { 200: z.array(brainNoteSchema), ...guardErrors },
      summary: "The caller's own notes, newest first",
    },
    createNote: {
      method: 'POST',
      path: '/notes',
      body: noteBody,
      responses: { 201: brainNoteSchema, ...bodyUpstreamErrors },
      summary: 'Remember a fact',
    },
    updateNote: {
      method: 'PATCH',
      path: '/notes/:id',
      pathParams: idParams,
      body: noteBody,
      responses: { 200: brainNoteSchema, ...bodyTargetUpstreamErrors },
      summary: 'Reword a fact, re-embedding it',
    },
    deleteNote: {
      method: 'DELETE',
      path: '/notes/:id',
      pathParams: idParams,
      responses: { 204: c.noBody(), ...targetErrors },
      summary: 'Forget a fact',
    },
    ask: {
      method: 'POST',
      path: '/ask',
      body: askBody,
      responses: { 200: brainAnswerSchema, ...bodyUpstreamErrors },
      summary:
        "Answer from the caller's notes alone, with no conversation history",
    },
  },
  { pathPrefix: '/brain' },
);
